/**
 * App configuration — environment-aware settings.
 *
 * In development: VITE_API_URL is empty, Vite proxy handles /api → localhost:8000
 * In production:  VITE_API_URL = "https://dukkan-api.azurewebsites.net"
 */

export const API_URL = (import.meta as any).env?.VITE_API_URL || '';

// ============================================================================
// Direct Blob Storage Access (bypasses API proxy for fast data loading)
// ============================================================================

let _blobBaseUrl: string | null = null
let _blobSasToken: string | null = null
let _blobTokenFetched = false

// scan-frames container (mobile uploads)
let _framesBaseUrl: string | null = null
let _framesSasToken: string | null = null

/**
 * Initialize direct blob access by fetching a container-level SAS token.
 * Also fetches the scan-frames token for mobile-uploaded scans.
 * Called once at app startup. If it fails, falls back to API proxy.
 */
export async function initBlobDirectAccess(): Promise<void> {
  if (_blobTokenFetched) return
  _blobTokenFetched = true
  const authToken = localStorage.getItem('dukkan_token')
  const headers: Record<string, string> = {}
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`

  // scan-data (COLMAP results)
  try {
    const response = await fetch(apiUrl('/api/scan-data-token'), { headers })
    if (response.ok) {
      const data = await response.json()
      _blobBaseUrl = data.base_url
      _blobSasToken = data.sas_token
      console.log(`⚡ [config] Direct blob access enabled: ${_blobBaseUrl}`)
    } else {
      console.warn(`⚠️ [config] Failed to get blob token (${response.status}), falling back to API proxy`)
    }
  } catch (error) {
    console.warn(`⚠️ [config] Failed to get blob token, falling back to API proxy:`, error)
  }

  // scan-frames (mobile uploads)
  try {
    const response = await fetch(apiUrl('/api/scan-frames-token'))
    if (response.ok) {
      const data = await response.json()
      _framesBaseUrl = data.baseUrl
      _framesSasToken = data.sasToken
      console.log(`⚡ [config] Scan-frames direct access enabled: ${_framesBaseUrl}`)
    }
  } catch {
    // non-critical — mobile scans will still load if container is public
  }
}

/**
 * Check if direct blob access is available.
 */
export function hasBlobDirectAccess(): boolean {
  return _blobBaseUrl !== null && _blobSasToken !== null
}

/** Return the blob container base URL (no trailing slash, no SAS token). */
export function getBlobBaseUrl(): string | null { return _blobBaseUrl }

/** Return the raw SAS token string (without leading '?'). */
export function getBlobSasToken(): string | null { return _blobSasToken }

/**
 * Prefix a path with the API server URL.
 * e.g. apiUrl('/api/shops') → "https://dukkan-api.azurewebsites.net/api/shops" in prod
 *                            → "/api/shops" in dev
 */
export function apiUrl(path: string): string {
  return `${API_URL}${path}`;
}

/**
 * Resolve a data URL for scan data files.
 * 
 * When direct blob access is enabled, converts /api/scan-data/ paths to direct
 * Azure Blob Storage URLs (without SAS — use toBlobUrl() for the final URL).
 * This is safe against further path appending (e.g., frames dir + filename).
 */
export function resolveUrl(path: string): string {
  if (!path) return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  
  // Direct blob access: convert known prefixes to direct Azure Blob Storage URLs
  // SAS token is NOT appended here — it's added by toBlobUrl() at fetch/img time
  if (_blobBaseUrl && _blobSasToken) {
    if (path.startsWith('/api/scan-data/')) {
      const blobPath = path.substring('/api/scan-data/'.length)
      return `${_blobBaseUrl}/${blobPath}`
    }
    // /reconstructions/ paths map to the same blob container (server treats both identically)
    if (path.startsWith('/reconstructions/')) {
      const blobPath = path.substring('/reconstructions/'.length)
      return `${_blobBaseUrl}/${blobPath}`
    }
  }
  
  // Fallback to API proxy
  if (!API_URL) return path;
  if (path.startsWith('/api/')) return `${API_URL}${path}`;
  // /reconstructions/ fallback: route through /api/scan-data/ proxy
  if (path.startsWith('/reconstructions/')) {
    const blobPath = path.substring('/reconstructions/'.length)
    return `${API_URL}/api/scan-data/${blobPath}`;
  }
  return path;
}

/**
 * Check if a URL points to Azure Blob Storage (scan-data or scan-frames container).
 */
export function isBlobUrl(url: string): boolean {
  return (!!_blobBaseUrl && url.startsWith(_blobBaseUrl)) ||
         (!!_framesBaseUrl && url.startsWith(_framesBaseUrl))
}

/**
 * Append SAS token to a resolved blob URL for actual fetching.
 * Handles both scan-data (COLMAP) and scan-frames (mobile) containers.
 */
export function toBlobUrl(url: string): string {
  if (_blobBaseUrl && _blobSasToken && url.startsWith(_blobBaseUrl)) {
    if (url.includes(_blobSasToken.substring(0, 20))) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${_blobSasToken}`
  }
  if (_framesBaseUrl && _framesSasToken && url.startsWith(_framesBaseUrl)) {
    if (url.includes(_framesSasToken.substring(0, 20))) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${_framesSasToken}`
  }
  return url
}

/**
 * Blob-aware fetch: for blob URLs, appends SAS token and strips Authorization
 * headers (Azure Blob Storage returns 400 if both SAS and auth header are sent).
 * For non-blob URLs, passes through unchanged.
 */
export function blobFetch(url: string, init?: RequestInit): Promise<Response> {
  if (!isBlobUrl(url)) {
    return fetch(url, init)
  }
  // Blob URL: add SAS token, strip Authorization header
  const finalUrl = toBlobUrl(url)
  if (init?.headers) {
    const h = new Headers(init.headers)
    h.delete('Authorization')
    return fetch(finalUrl, { ...init, headers: h })
  }
  return fetch(finalUrl, init)
}
